const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));

const hexToRgb = hex => {
  const normalized = hex.replace('#', '');
  const parsed =
    normalized.length === 3
      ? normalized
          .split('')
          .map(char => char + char)
          .join('')
      : normalized;

  return {
    r: parseInt(parsed.slice(0, 2), 16),
    g: parseInt(parsed.slice(2, 4), 16),
    b: parseInt(parsed.slice(4, 6), 16)
  };
};

const interpolateHex = (startHex, endHex, ratio) => {
  const safeRatio = clamp(ratio);
  const start = hexToRgb(startHex);
  const end = hexToRgb(endHex);

  const mixChannel = (from, to) => Math.round(from + (to - from) * safeRatio);

  return {
    r: mixChannel(start.r, end.r),
    g: mixChannel(start.g, end.g),
    b: mixChannel(start.b, end.b)
  };
};

const resolveRgbFromStops = (stops, ratio) => {
  const safeRatio = clamp(ratio);

  if (!stops.length) {
    return { r: 148, g: 163, b: 184 };
  }

  if (safeRatio <= stops[0][0]) {
    return hexToRgb(stops[0][1]);
  }

  for (let index = 1; index < stops.length; index += 1) {
    const [currentStop, currentColor] = stops[index];
    const [previousStop, previousColor] = stops[index - 1];

    if (safeRatio <= currentStop) {
      const segmentRatio =
        (safeRatio - previousStop) / (currentStop - previousStop || 1);
      return interpolateHex(previousColor, currentColor, segmentRatio);
    }
  }

  return hexToRgb(stops[stops.length - 1][1]);
};

export const RETENTION_HEATMAP_STOPS_LIGHT = [
  [0, '#eff6ff'],
  [0.28, '#dbeafe'],
  [0.52, '#7dd3fc'],
  [0.76, '#86efac'],
  [1, '#fde68a']
];

export const RETENTION_HEATMAP_STOPS_DARK = [
  [0, '#1b2635'],
  [0.3, '#1e3a8a'],
  [0.56, '#155e75'],
  [0.8, '#166534'],
  [1, '#a16207']
];

export const getRetentionHeatmapStops = isDarkMode =>
  isDarkMode ? RETENTION_HEATMAP_STOPS_DARK : RETENTION_HEATMAP_STOPS_LIGHT;

export const getCohortCellTheme = isDarkMode => ({
  fill: isDarkMode ? '#1b2635' : '#eff6ff',
  border: isDarkMode ? '#334155' : '#bfdbfe',
  label: isDarkMode ? '#cbd5e1' : '#0f172a'
});

export const getHeatmapDataLabelColor = ({
  value,
  isDarkMode,
  isCohortValue = false
}) => {
  if (isCohortValue) {
    return getCohortCellTheme(isDarkMode).label;
  }

  if (!Number.isFinite(value)) {
    return isDarkMode ? '#94a3b8' : '#64748b';
  }

  return isDarkMode ? '#e2e8f0' : '#0f172a';
};

const TABLE_HEATMAP_STOPS_LIGHT = [
  [0, '#eff6ff'],
  [0.32, '#bfdbfe'],
  [0.56, '#7dd3fc'],
  [0.78, '#86efac'],
  [1, '#fde68a']
];

const TABLE_HEATMAP_STOPS_DARK = [
  [0, '#172033'],
  [0.3, '#1d4ed8'],
  [0.55, '#0891b2'],
  [0.78, '#059669'],
  [1, '#ca8a04']
];

export const getTableHeatColor = (value, min, max, isDarkMode) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return 'transparent';

  const ratio = clamp((value - min) / (max - min || 1));
  const { r, g, b } = resolveRgbFromStops(
    isDarkMode ? TABLE_HEATMAP_STOPS_DARK : TABLE_HEATMAP_STOPS_LIGHT,
    ratio
  );
  const alpha = isDarkMode ? 0.2 + ratio * 0.45 : 0.3 + ratio * 0.4;

  return `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(3)})`;
};

export const getTableEmptyCellColor = ({
  isDarkMode,
  isIncompleteColumn = false
}) => {
  if (isIncompleteColumn) {
    return isDarkMode ? '#334155' : '#f3f4f6';
  }

  return isDarkMode ? '#1b2635' : '#f8fafc';
};
